/**
 * The control-rule route table (design §5.9).
 *
 * Mounted under `/controls` rather than under `/settings` on purpose: the
 * override report and the reconciliation queues are **worked**, not configured,
 * and burying them in settings would put a daily control review three clicks
 * deeper than the thing it reviews.
 *
 * Every route is gated on a read permission. `rules.override.read` belongs to
 * the Auditor role, so `/controls/overrides` opens for somebody who can change
 * nothing at all — which is the point of an audit artefact.
 */

import type { RouteRecordRaw } from 'vue-router'

import { RULES_PERMISSIONS } from '@/modules/rules/permissions'

export const RULES_ROUTES: RouteRecordRaw[] = [
  {
    path: '/controls/rules',
    name: 'rules-controls',
    component: () => import('@/modules/rules/ControlRulesView.vue'),
    meta: { titleKey: 'rules.routeTitles.controls', permission: RULES_PERMISSIONS.controlRead },
  },
  {
    path: '/controls/reason-codes',
    name: 'rules-reason-codes',
    component: () => import('@/modules/rules/ReasonCodesView.vue'),
    meta: { titleKey: 'rules.routeTitles.reasons', permission: RULES_PERMISSIONS.reasonRead },
  },
  {
    path: '/controls/overrides',
    name: 'rules-overrides',
    component: () => import('@/modules/rules/OverrideReportView.vue'),
    meta: { titleKey: 'rules.routeTitles.overrides', permission: RULES_PERMISSIONS.overrideRead },
  },
  {
    path: '/controls/queues',
    name: 'rules-queues',
    component: () => import('@/modules/rules/QueuesView.vue'),
    meta: { titleKey: 'rules.routeTitles.queues', permission: RULES_PERMISSIONS.queueRead },
  },
  {
    // A queue is addressed by its code, which is module-dotted
    // (`opcal.forced_close`), so the segment allows dots and underscores and
    // nothing that could be read as a path.
    path: '/controls/queues/:code([a-z0-9_.]+)',
    name: 'rules-queue-items',
    component: () => import('@/modules/rules/QueueItemsView.vue'),
    meta: { titleKey: 'rules.routeTitles.queueItems', permission: RULES_PERMISSIONS.queueRead },
  },
]
